import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Spin, Image } from "antd";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import wsuiApi from "@api/wsuiApi";
import styles from "./index.module.less";
import type { IPreConsultationDetail } from "@typing/wsui/preConsultation";

/** 从 markdown 内容中按出现顺序提取图片地址，用于预览时左右切换 */
const extractImageUrls = (content: string): string[] => {
    const urls: string[] = [];
    const reg = /!\[[^\]]*\]\(\s*<?([^\s)>]+)>?[^)]*\)/g;
    let match = reg.exec(content);
    while (match) {
        urls.push(match[1]);
        match = reg.exec(content);
    }
    return urls;
};

/**
 * 预问诊详情页面
 * 展示接口返回的 clinical_features（markdown 格式的预问诊小结）
 */
const PreConsultation: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");
    const [detail, setDetail] = useState<IPreConsultationDetail | null>(null);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [previewIndex, setPreviewIndex] = useState<number>(0);

    const content = detail?.clinical_features || "";
    const imageList = useMemo(() => extractImageUrls(content), [content]);

    const markdownComponents = useMemo<Components>(
        () => ({
            img: ({ src, alt }) => {
                const openPreview = (): void => {
                    const index = imageList.indexOf(String(src || ""));
                    setPreviewIndex(index > -1 ? index : 0);
                    setPreviewVisible(true);
                };
                return (
                    <img
                        className={styles.markdownImage}
                        src={typeof src === "string" ? src : undefined}
                        alt={alt || ""}
                        role="button"
                        tabIndex={0}
                        onClick={openPreview}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openPreview();
                            }
                        }}
                    />
                );
            },
        }),
        [imageList]
    );

    useEffect(() => {
        // 该页面 PC/H5 自适应，需解除 layout 对 body 的最小宽度与滚动限制
        document.body.classList.add(styles.responsiveBody);
        return () => document.body.classList.remove(styles.responsiveBody);
    }, []);

    useEffect(() => {
        const fetchDetail = async (): Promise<void> => {
            if (!id) {
                setError("缺少必要参数");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");

            try {
                const response = await wsuiApi.getPreConsultationDetail({ preconsultId: Number(id) });

                if (response.errno === 0 && response.data) {
                    setDetail(response.data);
                } else {
                    setError(response.errmsg || "获取数据失败");
                }
            } catch (err) {
                console.error("获取预问诊详情失败:", err);
                setError("网络请求失败，请稍后重试");
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [id]);

    if (loading) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.loadingWrapper}>
                    <Spin />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorWrapper}>{error}</div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.markdownBody}>
                <ReactMarkdown
                    remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkBreaks]}
                    components={markdownComponents}
                >
                    {content}
                </ReactMarkdown>
            </div>

            {/* 图片点击放大预览，支持左右切换 */}
            <Image.PreviewGroup
                items={imageList}
                preview={{
                    visible: previewVisible,
                    current: previewIndex,
                    onChange: setPreviewIndex,
                    onVisibleChange: setPreviewVisible,
                }}
            />
        </div>
    );
};

export default PreConsultation;
